<template>
  <div :class="ns.b()">
    <section :class="ns.e('list')">
      <z-grid
        ref="gridRef"
        :class="ns.e('grid')"
        :options="listGridOptions"
        :editable="true"
        :auto-height="false"
        :border="true"
      />
    </section>

    <z-dialog
      v-model="formVisible"
      :title="formDialogTitle"
      width="960px"
      align-center
      lock-scroll
      :class="ns.e('dialog')"
      destroy-on-close
      @closed="handleFormDialogClosed"
    >
      <section :class="[ns.e('form'), ns.em('form', 'dialog')]">
        <div :class="ns.e('form-row')">
          <label :class="[ns.e('label'), ns.is('required', true)]">{{ t('z.rules.ruleName') }}</label>
          <z-input
            v-model="draft.ruleName"
            :disabled="disabled"
            :placeholder="t('z.rules.ruleNamePlaceholder')"
            :class="ns.e('name-input')"
          />
        </div>

        <div :class="ns.e('form-row')">
          <label :class="ns.e('label')">{{ t('z.rules.ruleCondition') }}</label>
          <div :class="[ns.e('field-body'), ns.em('field-body', 'condition')]">
            <RulesConditions
              ref="conditionsRef"
              v-model="draft.rulesJson"
              :system="system"
              :module="module"
              :disabled="disabled"
              :show-summary="false"
            />
          </div>
        </div>

        <div :class="ns.e('form-row')">
          <label :class="ns.e('label')">{{ t('z.rules.ruleDescription') }}</label>
          <div
            :class="[ns.e('field-body'), ns.e('desc-readonly'), ns.is('empty', !autoRuleDescription)]"
          >
            {{ autoRuleDescription || t('z.rules.ruleDescriptionAuto') }}
          </div>
        </div>

        <div v-if="type === 'system'" :class="ns.e('form-row')">
          <label :class="ns.e('label')">{{ t('z.rules.contentType') }}</label>
          <z-button-group :class="ns.e('content-group')">
            <z-button
              v-for="item in systemContentOptions"
              :key="item"
              :type="systemContent === item ? 'primary' : 'default'"
              :disabled="disabled"
              @click="systemContent = item"
            >
              {{ t(`z.rules.content.${item}`) }}
            </z-button>
          </z-button-group>
        </div>

        <div v-if="effectiveContent !== 'boolean'" :class="ns.e('form-row')">
          <label :class="ns.e('label')">{{ t('z.rules.ruleResult') }}</label>
          <div :class="[ns.e('field-body'), ns.em('field-body', 'result')]">
            <RuleResult
              ref="resultRef"
              v-model="draft.resultJson"
              :mode="effectiveContent === 'error' ? 'error' : 'result'"
              :rule-result="ruleResult"
              :disabled="disabled"
            >
              <template v-if="$slots.result" #default="slotProps">
                <slot name="result" v-bind="slotProps" />
              </template>
            </RuleResult>
          </div>
        </div>
      </section>

      <template #footer>
        <div :class="ns.e('form-actions')">
          <z-button :disabled="disabled" @click="resetDraftInDialog">
            {{ t('z.rules.clear') }}
          </z-button>
          <z-button @click="closeFormDialog">
            {{ t('z.messagebox.cancel') }}
          </z-button>
          <z-button
            type="primary"
            :disabled="disabled || saving"
            :loading="saving"
            @click="saveRule"
          >
            {{ t('z.rules.saveRule') }}
          </z-button>
        </div>
      </template>
    </z-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, reactive, ref, watch } from 'vue'
import ZButton, { ZButtonGroup } from '@zoryn/components/button'
import ZDialog from '@zoryn/components/dialog'
import ZGrid, { GRID_HTTP_INJECTION_KEY } from '@zoryn/components/grid'
import { ZMessage } from '@zoryn/components/message'
import { CirclePlusFilled, CopyDocument, Delete } from '@zoryn/icons'
import { useLocale, useNamespace } from '@zoryn/hooks'
import type { HttpRequest } from '@zoryn/network'
import { buildRuleBindingPayload } from './rule-aviator'
import RuleResult from './rule-result.vue'
import RulesConditions from './rules-conditions.vue'
import { createRuleId } from './rule-group'
import {
  deleteRules,
  insertRule as insertRuleApi,
  isPersistedRule,
  queryRulesList,
  updateRule as updateRuleApi,
} from './rules-api'
import { createRulesListGridOptions, buildRulesListColumns } from './rules-grid'
import { formatRuleDescription } from './rule-display'
import { validateRuleDraft } from './rule-validate'
import { rulesEmits, rulesProps } from './rules-component'

import type { FilterPayloadNode } from '@zoryn/components/grid'
import {
  RULE_ERROR_MESSAGE_KEY,
  RULE_RESULT_VALUE_KEY,
  type RuleContentType,
  type RuleRecord,
  type RuleResultSchema,
  type RuleResultValue,
} from './rules'

const systemContentOptions: RuleContentType[] = ['error', 'boolean', 'result']

defineOptions({
  name: 'ZRules',
})

const props = defineProps(rulesProps)
const emit = defineEmits(rulesEmits)

const ns = useNamespace('rules')
const { t } = useLocale()
const request = inject<HttpRequest | null>(GRID_HTTP_INJECTION_KEY, null)

const conditionsRef = ref<InstanceType<typeof RulesConditions>>()
const resultRef = ref<InstanceType<typeof RuleResult>>()
interface RulesGridInstance {
  loadTableData?: (datas: Record<string, unknown>[]) => Promise<void>
}

const gridRef = ref<RulesGridInstance>()
const editingId = ref<string | null>(null)
const saving = ref(false)
const currentRow = ref<RuleRecord | null>(null)
const formVisible = ref(false)

const formDialogTitle = computed(() =>
  editingId.value ? t('z.rules.editRuleDialog') : t('z.rules.newRuleDialog')
)

const listGridOptions = createRulesListGridOptions(t, props.type, {
  onCurrentRowChange: (row) => {
    currentRow.value = row
  },
  onEnabledChange: (row, enabled) => {
    if (props.disabled) return
    void updateRuleEnabled(row, enabled)
  },
})

listGridOptions.btns = [
  {
    label: t('z.table.toolbar.add'),
    icon: CirclePlusFilled,
    type: 'primary',
    onClick: () => openCreateDialog(),
  },
  {
    label: t('z.table.toolbar.copy'),
    icon: CopyDocument,
    type: 'primary',
    onClick: () => copySelectedRule(),
  },
  {
    label: t('z.rules.delete'),
    icon: Delete,
    type: 'warning',
    onClick: () => deleteSelected(),
  },
  {
    label: t('z.rules.edit'),
    onClick: () => openEditDialog(),
  },
]

watch(
  () => props.type,
  (type) => {
    listGridOptions.columns = buildRulesListColumns(t, type)
  }
)

const selectedRows = computed(
  () =>
    (listGridOptions.getAllCheckbox?.() ?? []) as unknown as RuleRecord[]
)

const records = computed({
  get: () => props.modelValue ?? [],
  set: (val) => {
    emit('update:modelValue', val)
    emit('change', val)
  },
})

const systemContent = ref<RuleContentType>('boolean')

const effectiveContent = computed<RuleContentType>(() =>
  props.type === 'user' ? props.content : systemContent.value
)

function inferContentFromResultJson(resultJson: RuleResultValue): RuleContentType {
  const message = resultJson[RULE_ERROR_MESSAGE_KEY]
  if (message != null && message !== '') {
    return 'error'
  }
  const value = resultJson[RULE_RESULT_VALUE_KEY]
  if (value != null && value !== '') {
    return 'result'
  }
  return 'boolean'
}

function defaultByType(columnType: RuleResultSchema['type']) {
  if (columnType === 'switch') return false
  if (columnType === 'number') return undefined
  return null
}

function createEmptyResult(): RuleResultValue {
  if (effectiveContent.value === 'boolean') {
    return {}
  }
  if (effectiveContent.value === 'error') {
    return { [RULE_ERROR_MESSAGE_KEY]: '' }
  }
  if (props.ruleResult) {
    return {
      [RULE_RESULT_VALUE_KEY]:
        props.ruleResult.defaultValue !== undefined
          ? props.ruleResult.defaultValue
          : defaultByType(props.ruleResult.type),
    }
  }
  return {}
}

const draft = reactive({
  ruleName: '',
  rulesJson: null as FilterPayloadNode | FilterPayloadNode[] | null,
  resultJson: createEmptyResult(),
})

const autoRuleDescription = computed(() => {
  void draft.rulesJson
  void draft.resultJson
  void effectiveContent.value
  const conditionText = conditionsRef.value?.getDescription?.() ?? ''
  return formatRuleDescription(
    conditionText,
    effectiveContent.value,
    draft.resultJson,
    props.ruleResult
  )
})

async function syncGridRecords(
  data: RuleRecord[] = records.value
) {
  await nextTick()
  await gridRef.value?.loadTableData?.(
    data as unknown as Record<string, unknown>[]
  )
  syncCurrentRowFromGrid()
}

function syncCurrentRowFromGrid() {
  currentRow.value = (listGridOptions.getCurrentRow?.() ?? null) as RuleRecord | null
}

function resolveMapRow(): RuleRecord | null {
  return (
    currentRow.value ??
    ((listGridOptions.getCurrentRow?.() ?? null) as RuleRecord | null)
  )
}

async function refreshList() {
  if (!request) {
    await syncGridRecords()
    return
  }
  try {
    const list = await queryRulesList(request, {
      system: props.system,
      module: props.module,
    })
    records.value = list
    await syncGridRecords(list)
  } catch (err) {
    ZMessage.error((err as Error)?.message ?? t('z.rules.loadFailed'))
  }
}

watch(
  () => [props.system, props.module] as const,
  () => {
    void refreshList()
  }
)

watch(
  () => props.modelValue,
  (val) => {
    if (!request) {
      void syncGridRecords(val ?? [])
    }
  },
  { deep: true }
)

onMounted(() => {
  void refreshList()
})

watch(
  () => [props.type, props.content, props.ruleResult, effectiveContent.value] as const,
  () => {
    if (!editingId.value) {
      draft.resultJson = createEmptyResult()
    }
  },
  { deep: true }
)

function deleteSelected() {
  void deleteSelectedRows()
}

async function deleteSelectedRows() {
  const selectedIds = new Set(
    selectedRows.value.map((item) => item.id)
  )
  const removed = records.value.filter((item) => selectedIds.has(item.id))
  if (!removed.length) {
    ZMessage.warning(t('z.table.deleteNoSelection'))
    return
  }

  const persisted = removed.filter((item) => isPersistedRule(item))

  if (request && persisted.length) {
    try {
      await deleteRules(request, persisted)
    } catch (err) {
      ZMessage.error((err as Error)?.message ?? t('z.rules.deleteFailed'))
      return
    }
    if (editingId.value && selectedIds.has(editingId.value)) {
      closeFormDialog()
    }
    emit('delete', removed)
    ZMessage.success(t('z.rules.deleteSuccess'))
    await refreshList()
    return
  }

  const next = records.value.filter((item) => !selectedIds.has(item.id))
  records.value = next
  await syncGridRecords(next)
  if (editingId.value && selectedIds.has(editingId.value)) {
    closeFormDialog()
  }
  emit('delete', removed)
}

async function updateRuleEnabled(row: RuleRecord, enabled: boolean) {
  const nextRecord = { ...row, enabled }
  const next = records.value.map((item) =>
    item.id === row.id ? nextRecord : item
  )
  records.value = next
  await syncGridRecords(next)

  if (!request || !isPersistedRule(row)) {
    return
  }

  try {
    await updateRuleApi(request, nextRecord)
  } catch (err) {
    const rollback = records.value.map((item) =>
      item.id === row.id ? row : item
    )
    records.value = rollback
    await syncGridRecords(rollback)
    ZMessage.error((err as Error)?.message ?? t('z.rules.saveFailed'))
  }
}

function openCreateDialog() {
  if (props.disabled) return
  clearDraft()
  formVisible.value = true
}

function openEditDialog() {
  if (props.disabled) return
  const row = resolveMapRow()
  if (!row) {
    ZMessage.warning(t('z.rules.selectRuleFirst'))
    return
  }
  currentRow.value = row
  selectRow(row)
  formVisible.value = true
  emit('map', [row])
}

function copySelectedRule() {
  if (props.disabled) return
  const row = resolveMapRow()
  if (!row) {
    ZMessage.warning(t('z.rules.selectRuleFirst'))
    return
  }
  currentRow.value = row
  fillDraftFromRow(row)
  editingId.value = null
  formVisible.value = true
}

function closeFormDialog() {
  formVisible.value = false
}

function handleFormDialogClosed() {
  clearDraft()
}

function resetDraftInDialog() {
  if (editingId.value) {
    const row = records.value.find((item) => item.id === editingId.value)
    if (row) {
      selectRow(row)
      return
    }
  }
  clearDraft()
}

function cloneRulesJson(
  value: FilterPayloadNode | FilterPayloadNode[] | null
): FilterPayloadNode | FilterPayloadNode[] | null {
  if (value == null) {
    return null
  }
  return JSON.parse(JSON.stringify(value)) as FilterPayloadNode | FilterPayloadNode[]
}

function selectRow(row: RuleRecord) {
  if (props.disabled) return
  editingId.value = row.id
  fillDraftFromRow(row)
}

function fillDraftFromRow(row: RuleRecord) {
  draft.ruleName = row.ruleName
  draft.rulesJson = cloneRulesJson(row.rulesJson)
  draft.resultJson = { ...row.resultJson }
  if (props.type === 'system') {
    systemContent.value = row.content ?? inferContentFromResultJson(row.resultJson)
  }
}

function clearDraft() {
  editingId.value = null
  draft.ruleName = ''
  draft.rulesJson = null
  draft.resultJson = createEmptyResult()
  resultRef.value?.reset()
}

function buildRecord(): RuleRecord {
  const binding = buildRuleBindingPayload(
    draft.rulesJson,
    { ...draft.resultJson },
    {
      ruleName: draft.ruleName,
      content: effectiveContent.value,
      resultField: RULE_RESULT_VALUE_KEY,
    }
  )

  return {
    id: editingId.value ?? createRuleId('rule'),
    system: props.system,
    module: props.module,
    ruleName: draft.ruleName.trim(),
    ruleDescription: autoRuleDescription.value.trim(),
    rulesJson: binding.rulesJson,
    resultJson: binding.resultJson,
    content: effectiveContent.value,
    ruleScript: binding.ruleScript,
    ruleExpr: binding.ruleExpr,
    scriptLanguage: binding.scriptLanguage,
    enabled: true,
    priority: records.value.length + 1,
  }
}

async function saveRule() {
  const validationKey = validateRuleDraft({
    ruleName: draft.ruleName,
    content: effectiveContent.value,
    resultJson: draft.resultJson,
    ruleResult: props.ruleResult,
  })
  if (validationKey) {
    ZMessage.warning(t(`z.rules.${validationKey}`))
    return
  }

  const record = buildRecord()
  saving.value = true

  try {
    if (request) {
      if (editingId.value && isPersistedRule({ id: editingId.value })) {
        await updateRuleApi(request, record)
        ZMessage.success(t('z.rules.saveSuccess'))
      } else {
        const saved = await insertRuleApi(request, record)
        emit('add', saved)
        ZMessage.success(t('z.rules.saveSuccess'))
      }
      closeFormDialog()
      await refreshList()
      return
    }

    let next: RuleRecord[]
    if (editingId.value) {
      next = records.value.map((item) =>
        item.id === editingId.value ? { ...record, priority: item.priority } : item
      )
    } else {
      next = [...records.value, record]
      emit('add', record)
    }
    records.value = next
    await syncGridRecords(next)
    closeFormDialog()
  } catch (err) {
    ZMessage.error((err as Error)?.message ?? t('z.rules.saveFailed'))
  } finally {
    saving.value = false
  }
}

defineExpose({
  clearDraft,
  closeFormDialog,
  copySelectedRule,
  getRecords: () => records.value,
  openCreateDialog,
  openEditDialog,
  reload: () => conditionsRef.value?.reload(),
  refreshList,
})
</script>
